import { Group, Stack } from '@mantine/core';
import * as Sentry from '@sentry/browser';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { activate } from '../../../api/auth-client/auth-client';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { baseWhite, inkLight, primaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';
import { Layout } from '../layout';

enum ActivationState {
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

export const Activation = () => {
  useDocumentTitle('Activate Account - Bloomfilter');

  const navigate = useNavigate();
  const { uid, token } = useParams<{ uid: string; token: string }>();
  const [activationState, setActivationState] = useState<ActivationState>(ActivationState.Loading);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const activationMutation = useMutation({
    mutationKey: ['activation', { uid, token }] as const,
    mutationFn: async () => {
      if (!uid || !token) {
        throw new Error('Missing activation parameters');
      }
      return activate(uid, token);
    },
    onSuccess: () => {
      setActivationState(ActivationState.Success);
    },
    onError: (error: { error: any }) => {
      setActivationState(ActivationState.Error);
      setErrorMessage(
        error.error?.detail ||
          'This activation link is invalid or has expired. Please contact support@bloomfilter.app for assistance.',
      );
      Sentry.captureException(error);
    },
  });

  useEffect(() => {
    if (uid && token) {
      activationMutation.mutate();
    } else {
      setActivationState(ActivationState.Error);
      setErrorMessage('Invalid activation link. Please check your email and try again.');
    }
  }, [uid, token, activationMutation]);

  const handleContinue = () => {
    if (activationState === ActivationState.Success) {
      // Navigate to password creation since users have unusable passwords initially
      navigate('/reset-password');
    }
  };

  const handleBackToSignIn = () => {
    navigate('/');
  };

  const renderContent = () => {
    switch (activationState) {
      case ActivationState.Loading:
        return (
          <>
            <Group gap={16}>
              <Icon name="hourglass_empty" size={32} color={primaryBase} />
              <H3>Activating Your Account</H3>
            </Group>
            <Text>Please wait while we activate your account...</Text>
          </>
        );

      case ActivationState.Success:
        return (
          <>
            <Group gap={16}>
              <Icon name="check_circle" size={32} color={primaryBase} />
              <H3>Account Activated Successfully!</H3>
            </Group>
            <Text>Your account has been activated. You'll now need to create a password to complete your setup.</Text>
            <Button
              onClick={handleContinue}
              w="100%"
              radius="xl"
              rightSection={<Icon name="arrow_forward" size={18} color={baseWhite} />}
            >
              Set Up Password
            </Button>
          </>
        );

      case ActivationState.Error:
        return (
          <>
            <Group gap={16}>
              <Icon name="error" size={32} color="#E53E3E" />
              <H3>Activation Failed</H3>
            </Group>
            <Text>{errorMessage}</Text>
            <Text size="small" color={inkLight}>
              Need help? Contact us at support@bloomfilter.app
            </Text>
            <Button
              onClick={handleBackToSignIn}
              variant="outline"
              w="100%"
              radius="xl"
              rightSection={<Icon name="arrow_back" size={18} />}
            >
              Back to Sign In
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <Stack gap={32}>{renderContent()}</Stack>
    </Layout>
  );
};
